import {
	CloseThin,
	RadioButtonEmptyWhite,
	RadioButtonSelectedGold,
	SearchHomeUnfilled
} from '@/resources/assets/images';
import React, { useState } from 'react';
import './SearchTabSearch.scss';
import { shallowEqual, useSelector } from 'react-redux';

import type { RootState } from '@/rootReducer';
import type { BusinessQuery } from '@/modules/dashboard/dashboardTypes';
import { sendAnalytics } from '@/services/analyticsService';
import { isQueryDifferent } from '../src/helpers/isQueryDifferent';
import { SearchFilter, useSearchTabContext } from '../SearchTabContext';
import SearchTabFiltersListItemHeader from '../SearchTabFiltersListItemHeader';
import SearchTabSearchBar from './SearchTabSearchBar/SearchTabSearchBar';

type SearchTabSearchProps = {
	onFilterChange: (query: BusinessQuery) => void;
	onGetBusiness: (query: BusinessQuery) => void;
};

const SearchTabSearch = ({
	onFilterChange,
	onGetBusiness
}: SearchTabSearchProps) => {
	const [query, user] = useSelector(
		(state: RootState) => [
			state.dashboard.get('query').toJS(),
			state.user.getIn(['authorize', 'user']).toJS()
		],
		shallowEqual
	);

	const [includeSelected, setIncludeSelected] = useState<boolean>(true);
	const { selectedListItem } = useSearchTabContext();

	const invertIncludeExclude = (filter: SearchFilter) => {
		const { value, type, index } = filter;

		// 0 = include, 1 = exclude
		const newType = type === 0 ? 1 : 0;

		const searchesArray: NonNullable<BusinessQuery['searches']> =
			query.searches || [];

		searchesArray[index] = { value, type: newType, index };

		const excluded: Array<(typeof searchesArray)[0]['value']> = [];
		const included: Array<(typeof searchesArray)[0]['value']> = [];

		searchesArray.forEach(search => {
			if (search.type === 1) {
				return excluded.push(search.value);
			}

			return included.push(search.value);
		});

		const newQuery = {
			...query,
			searches: searchesArray,
			includes: included,
			excludes: excluded,
			user
		};

		if (user && user.email) {
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: 'User switched include/exclude',
				properties: {
					query: newQuery,
					invertedFilter: filter.value,
					querySearch: searchesArray,
					excludedFromSearch: excluded,
					includedInSearch: included
				}
			});
		}
		onFilterChange(newQuery);

		onGetBusiness(newQuery);
	};

	const removeFilterTag = (value: string) => {
		const queryNew = { ...query };
		const excluded = [];
		const included = [];

		if (queryNew?.searches?.length === 1) {
			delete queryNew.searches;
			delete queryNew.includes;
			delete queryNew.excludes;
		} else {
			// filter() removes, map() reindexes
			queryNew.searches = queryNew?.searches
				?.filter((search: SearchFilter) => search.value !== value)
				.map((search: SearchFilter, idx: number) => ({
					...search,
					index: idx
				}));
		}

		for (let i = 0; i < queryNew?.searches?.length; i++) {
			if (queryNew.searches[i].type === 1) {
				excluded.push(queryNew.searches[i].value);
			} else {
				included.push(queryNew.searches[i].value);
			}
		}

		queryNew.excludes = excluded;
		queryNew.includes = included;

		if (user && user.email) {
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: 'User removed single filter',
				properties: {
					query: queryNew,
					removedFilter: value,
					querySearch: queryNew.searches,
					excludedFromSearch: excluded,
					includedInSearch: included
				}
			});
		}

		onFilterChange(queryNew);

		const queryChanged = isQueryDifferent(queryNew);

		if (queryChanged) {
			onGetBusiness(queryNew);
		}
	};

	const clearSearchFilters = () => {
		const queryNew = { ...query };

		if (user && user.email) {
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: 'User cleared all search filters',
				properties: {
					query: queryNew,
					querySearch: queryNew.searches
				}
			});
		}

		delete queryNew.searches;
		delete queryNew.includes;
		delete queryNew.excludes;

		onFilterChange(queryNew);

		const queryChanged = isQueryDifferent(queryNew);

		if (queryChanged) {
			onGetBusiness(queryNew);
		}
	};

	return (
		<div
			className={`search-tab-filters-list-item ${selectedListItem === 'Search' ? 'opened' : ''} ${query?.searches?.length > 0 ? 'filter-selected' : ''} `}
		>
			<SearchTabFiltersListItemHeader
				filterName="Search"
				filterCount={query?.searches?.length}
				onClick={clearSearchFilters}
				displayClearFiltersButton={query?.searches?.length > 0}
				icon={
					<SearchHomeUnfilled className="search-tab-filter-list-item-icon search" />
				}
			/>
			<div className="search-tab-filters-list-item-dropdown search">
				<div className="search-tab-filters-list-item-dropdown-divider" />
				<div>Search for business names or categories</div>
				<div className="search-tab-filters-list-item-dropdown-radio-inputs">
					<span className="search-tab-filters-list-item-dropdown-radio-input-wrapper">
						{includeSelected ? (
							<RadioButtonSelectedGold />
						) : (
							<RadioButtonEmptyWhite
								onClick={() => {
									setIncludeSelected(true);
								}}
							/>
						)}
						<span>Include</span>
					</span>
					<span className="search-tab-filters-list-item-dropdown-radio-input-wrapper">
						{!includeSelected ? (
							<RadioButtonSelectedGold />
						) : (
							<RadioButtonEmptyWhite
								onClick={() => setIncludeSelected(false)}
							/>
						)}
						<span>Exclude</span>
					</span>
				</div>
				<SearchTabSearchBar
					includeSelected={includeSelected}
					onFilterChange={onFilterChange}
					onGetBusiness={onGetBusiness}
				/>
				<div className="search-tab-filters-search-tags">
					{query?.searches?.map((search: SearchFilter) => {
						const { type, value, index } = search;
						return (
							<span
								key={value + type + index}
								className="search-tab-filters-search-tag"
							>
								<button
									type="button"
									onClick={() => {
										invertIncludeExclude(search);
									}}
								>
									<span
										className={`search-tab-filters-search-tag-type ${type === 0 ? 'include' : 'exclude'}`}
									>
										{type === 0 ? 'Includes' : 'Excludes'}
									</span>
									{value}
								</button>
								<CloseThin
									onClick={() => removeFilterTag(value)}
									className="search-tab-filters-search-tag-close-icon"
								/>
							</span>
						);
					})}
				</div>
			</div>
		</div>
	);
};

export default SearchTabSearch;
